import Colors from './color';
import { IssueCategory, ISSUE_TYPES } from '../constants/issueTypes';

export interface IssueChipColors {
  backgroundColor: string;
  /** Always concrete (never undefined) so it drops straight into MUI `sx`. */
  color: string;
}

/**
 * HITL-1501: one solid colour per issue category, so the flag panel reads as
 * colour-coded groups. Same shape and intent as `intentButtonStyle.ts`, which
 * already solves this for the intent bar.
 *
 * ## Two constraints on these hues
 *
 * 1. **Never the intent bar's amber / blue / red** (`Colors.acknowledge`,
 *    `Colors.askRepeat`, `Colors.requestStaff`). Those mean Conversation, Order Flow
 *    and Escalation in the bar rendered directly below the panel, and a red issue
 *    chip reading as "escalation" is a real mis-parse, not a theoretical one.
 * 2. **Light tints on the panel's dark daintree ground**, all carrying
 *    `primaryButtonTextColor` ink. Every pairing below clears 4.5:1 against both the
 *    ink and the ground by more than double — lowest is `ai_response` at 9.26:1.
 *    `readback` is the tenth and does not displace it: `babyBlueEyes` scores 9.89:1
 *    on the ink and 8.78:1 on the daintree ground. It is the last unused member of
 *    the pastel family the others came from that is neither red (which would read as
 *    escalation, see constraint 1) nor a near-duplicate of `beeswax`. Its row sits
 *    second, far from the other two blues at rows eight and nine.
 *
 * `unclassified` is absent on purpose: it is the absence of a choice and never
 * renders as a chip.
 */
const INK = Colors.primaryButtonTextColor;

export const ISSUE_CATEGORY_SCHEME: Record<IssueCategory, IssueChipColors> = {
  [IssueCategory.storeBehavior]: {
    backgroundColor: Colors.apricot,
    color: INK,
  },
  [IssueCategory.readback]: {
    backgroundColor: Colors.babyBlueEyes,
    color: INK,
  },
  [IssueCategory.aiEscalation]: { backgroundColor: Colors.mauve, color: INK },
  [IssueCategory.aiOrder]: { backgroundColor: Colors.teaGreen, color: INK },
  [IssueCategory.aiResponse]: {
    backgroundColor: Colors.periwinkle,
    color: INK,
  },
  [IssueCategory.asr]: { backgroundColor: Colors.deepChampagne, color: INK },
  [IssueCategory.audio]: { backgroundColor: Colors.electricBlue, color: INK },
  [IssueCategory.pos]: { backgroundColor: Colors.spindle, color: INK },
  [IssueCategory.menuConfig]: { backgroundColor: Colors.beeswax, color: INK },
  [IssueCategory.other]: { backgroundColor: Colors.alto, color: INK },
  [IssueCategory.unclassified]: { backgroundColor: Colors.alto, color: INK },
};

/** Falls back to neutral rather than throwing. Defensive, not a config path. */
export const getIssueChipColors = (category: IssueCategory): IssueChipColors =>
  ISSUE_CATEGORY_SCHEME[category] ?? {
    backgroundColor: Colors.alto,
    color: INK,
  };

/**
 * Group headings. The code carries only snake_case enum values, so the display
 * strings live here.
 *
 * All ten are populated even though `unclassified` never renders — a partial map
 * would make the type lie.
 */
export const ISSUE_CATEGORY_LABELS: Record<IssueCategory, string> = {
  [IssueCategory.storeBehavior]: 'Store',
  [IssueCategory.readback]: 'Readback',
  [IssueCategory.aiEscalation]: 'AI Escalation',
  [IssueCategory.aiOrder]: 'AI Order',
  [IssueCategory.aiResponse]: 'AI Response',
  [IssueCategory.asr]: 'ASR',
  [IssueCategory.audio]: 'Audio',
  [IssueCategory.pos]: 'POS',
  [IssueCategory.menuConfig]: 'Menu',
  [IssueCategory.other]: 'Other',
  [IssueCategory.unclassified]: 'Unclassified',
};

/**
 * NO display-label map. The wire values are unprefixed now, so a chip reads correctly
 * as-is under its category heading — `Validation` under POS, `Escalation` under AI
 * ESCALATION, `Slow Response` under AI RESPONSE. A second set of strings would only
 * be somewhere for the two to drift apart.
 */
